import { type Query, type QueryClient } from "@tanstack/react-query";
import { createKeyedRevisionRefresh } from "@a2z/realtime";
import type { OnlineOrderCount } from "@/lib/api/types";
import { queryKeys } from "./queries";

/** One application-level deadline, including when only the header badge is mounted. */
export function watchOnlineOrderExpiry(client: QueryClient, isCurrent: () => boolean) {
  let disposed = false;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let lastData: OnlineOrderCount | undefined;
  let lastActive = false;
  let handledDeadline: string | undefined;
  let deadlineAttempts = 0;
  const current = () => !disposed && isCurrent();
  const refresh = createKeyedRevisionRefresh<Query>({ isCurrent: current, refresh: async (query) => {
    if (query.state.fetchStatus === "fetching") await query.promise?.catch(() => undefined);
    if (current() && query.isActive() && client.getQueryCache().get(query.queryHash) === query) {
      await client.refetchQueries({ queryKey: query.queryKey, exact: true, type: "active" }, { cancelRefetch: false });
    }
  } });
  const update = () => {
    const query = client.getQueryCache().find({ queryKey: queryKeys.onlineOrdersCount, exact: true });
    const data = query?.state.data as OnlineOrderCount | undefined;
    const active = query?.isActive() === true;
    if (data === lastData && active === lastActive) return;
    lastData = data;
    lastActive = active;
    clearTimeout(timer);
    timer = undefined;
    if (!current() || !active || !data?.requested || !data.nextExpiresAt || !data.serverTime) return;
    const remaining = Date.parse(data.nextExpiresAt) - Date.parse(data.serverTime);
    if (!Number.isFinite(remaining)) return;
    if (data.nextExpiresAt !== handledDeadline) deadlineAttempts = 0;
    // A just-before-expiry response may still name this deadline. At most
    // three padded reads per deadline — the first at the deadline plus two
    // retries from each new server time — never an idle loop.
    if (data.nextExpiresAt === handledDeadline && (remaining <= 0 || deadlineAttempts >= 3)) return;
    // Use the server's relative deadline, not the phone's wall-clock setting.
    const elapsed = Math.max(0, Date.now() - (query?.state.dataUpdatedAt ?? Date.now()));
    timer = setTimeout(() => {
      timer = undefined;
      if (!current()) return;
      handledDeadline = data.nextExpiresAt!;
      deadlineAttempts++;
      void client.invalidateQueries({ queryKey: queryKeys.onlineOrders, refetchType: "none" });
      client.getQueryCache().findAll({ queryKey: queryKeys.onlineOrders }).forEach((entry) => {
        if (entry.isActive()) refresh.enqueue(entry, { topics: ["onlineOrders"], catchUp: false });
      });
    }, Math.max(0, remaining - elapsed) + 100);
  };
  const stop = client.getQueryCache().subscribe((event) => {
    if (event.query.queryKey.length === queryKeys.onlineOrdersCount.length &&
      queryKeys.onlineOrdersCount.every((part, index) => event.query.queryKey[index] === part)) update();
  });
  update();
  return () => {
    disposed = true;
    clearTimeout(timer);
    refresh.dispose();
    stop();
  };
}
